// Infers a display color from a natural-language prompt.
// Returns a hex color string — DEFAULT_PART_COLOR for metal/unknown parts.

// Current default part color (cumulatively ~59% darker than the original
// #8A8A8A: four successive darkening passes — 20%, 20%, 15%, 25% — taking
// 138 → 110 → 88 → 75 → 56 = #8A → #6E → #58 → #4B → #38).
// All historical defaults are tracked in PART_DEFAULT_COLORS so:
//   • normalizePartColor() coerces past parts to the new default (no DB
//     migration needed);
//   • the VisualCard "isMetal" check works against any historical default.
export const DEFAULT_PART_COLOR = '#383838';
export const PART_DEFAULT_COLORS = new Set([
  '#383838', // current
  '#4B4B4B', // legacy
  '#585858', // legacy
  '#6E6E6E', // legacy
  '#8A8A8A', // legacy (original)
]);

export function normalizePartColor(color: string | null | undefined): string {
  if (!color) return DEFAULT_PART_COLOR;
  return PART_DEFAULT_COLORS.has(color.toUpperCase())
    ? DEFAULT_PART_COLOR
    : color;
}

const EXPLICIT_COLORS: Record<string, string> = {
  red: '#C0392B',
  crimson: '#DC143C',
  scarlet: '#FF2400',
  orange: '#E67E22',
  yellow: '#F1C40F',
  lime: '#32CD32',
  green: '#27AE60',
  teal: '#00897B',
  cyan: '#00BCD4',
  blue: '#2980B9',
  navy: '#001F5B',
  indigo: '#3F51B5',
  violet: '#8E44AD',
  purple: '#8E44AD',
  magenta: '#C2185B',
  pink: '#E91E8C',
  rose: '#FF0040',
  white: '#ECEFF1',
  cream: '#FFFDD0',
  ivory: '#FFFFF0',
  beige: '#F5F5DC',
  tan: '#D2B48C',
  brown: '#795548',
  black: '#1C1C1C',
  gold: '#FFD700',
  golden: '#FFD700',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
  copper: '#B87333',
  brass: '#B5A642',
  gray: DEFAULT_PART_COLOR,
  grey: DEFAULT_PART_COLOR,
};

// Object/material → representative hex color
const OBJECT_COLORS: Record<string, string> = {
  // ── Fruits ───────────────────────────────────────────
  banana: '#FFE135',
  lemon: '#FFF44F',
  lime: '#32CD32',
  orange: '#E67E22',
  tangerine: '#F28500',
  clementine: '#E8821C',
  grapefruit: '#FF6347',
  apple: '#C91B21',
  pear: '#D1E231',
  strawberry: '#FC0035',
  raspberry: '#E30B5D',
  blueberry: '#4F86C6',
  blackberry: '#3D0C02',
  grape: '#6F2DA8',
  watermelon: '#4CAF50',
  cantaloupe: '#FF8C00',
  honeydew: '#F0FFF0',
  pineapple: '#F9A835',
  mango: '#FF9A00',
  papaya: '#FF9248',
  peach: '#FFCBA4',
  apricot: '#FBCEB1',
  cherry: '#DE3163',
  plum: '#8E4585',
  prune: '#701C1C',
  kiwi: '#4A7C3F',
  coconut: '#8D6E63',
  avocado: '#568203',
  fig: '#7B4F8C',
  pomegranate: '#C40233',
  persimmon: '#EC5800',
  guava: '#FF6EFF',
  passion: '#FF004F',
  dragonfruit: '#FF007F',
  lychee: '#FFBFCB',

  // ── Vegetables ───────────────────────────────────────
  carrot: '#FF6B35',
  tomato: '#FF2D00',
  broccoli: '#236B2D',
  pumpkin: '#FF7518',
  squash: '#F4A460',
  corn: '#FBDD03',
  maize: '#FBDD03',
  eggplant: '#614051',
  aubergine: '#614051',
  cucumber: '#4E8822',
  zucchini: '#4CAF50',
  courgette: '#4CAF50',
  celery: '#7FC97F',
  asparagus: '#4E7245',
  artichoke: '#4E7245',
  broccoli_stem: '#4E7245',
  spinach: '#3D9140',
  lettuce: '#4CAF50',
  cabbage: '#4CAF50',
  kale: '#3D6B45',
  onion: '#8B7355',
  garlic: '#F5F5DC',
  potato: '#C4A35A',
  sweetpotato: '#CC5500',
  yam: '#CC5500',
  beet: '#8B0000',
  beetroot: '#8B0000',
  radish: '#E53935',
  turnip: '#E8D5C4',
  parsnip: '#F5DEB3',
  leek: '#4E7245',
  chive: '#4CAF50',
  pepper: '#E53935',
  bellpepper: '#E53935',
  jalapeño: '#228B22',
  jalapeno: '#228B22',
  habanero: '#FF4500',
  pea: '#4CAF50',
  edamame: '#6B8E23',
  broccoli_floret: '#236B2D',

  // ── Flowers / plants ─────────────────────────────────
  rose: '#FF0040',
  sunflower: '#FFD700',
  daisy: '#FFFACD',
  tulip: '#FF69B4',
  orchid: '#DA70D6',
  lavender: '#967BB6',
  violet: '#EE82EE',
  iris: '#5A4FCF',
  lily: '#FF6347',
  dandelion: '#FFD700',
  chrysanthemum: '#FFD700',
  poppy: '#FF0000',
  grass: '#4CAF50',
  leaf: '#4CAF50',
  leaves: '#4CAF50',
  fern: '#4F7942',
  moss: '#8A9A5B',
  cactus: '#4CAF50',
  succulent: '#5D8A5E',
  bamboo: '#8DB600',
  tree: '#2D5A1B',
  bark: '#966F33',
  log: '#8B5E3C',

  // ── Food / cooked ────────────────────────────────────
  cheese: '#FFD700',
  cheddar: '#FFA500',
  butter: '#FFDB58',
  egg: '#FFFDD0',
  yolk: '#FFD700',
  chocolate: '#7B3F00',
  cocoa: '#7B3F00',
  bread: '#D2A679',
  toast: '#B8864E',
  cookie: '#C68E5A',
  cake: '#F5DEB3',
  donut: '#C49A6C',
  pizza: '#FF6347',
  pasta: '#F5DEB3',
  rice: '#FFFACD',
  sushi: '#FF4500',
  salmon: '#FA8072',
  tuna: '#E07B54',
  shrimp: '#FF7F7F',
  lobster: '#E0351D',
  crab: '#E0351D',
  honey: '#FFA500',
  maple: '#964B00',
  caramel: '#C68642',
  vanilla: '#F3E5AB',

  // ── Precious / metals ────────────────────────────────
  gold: '#FFD700',
  silver: '#C0C0C0',
  copper: '#B87333',
  bronze: '#CD7F32',
  brass: '#B5A642',
  platinum: '#E5E4E2',
  titanium: '#878681',
  chrome: '#DBE4E8',
  rust: '#B7410E',
  iron: '#A19D94',
  steel: DEFAULT_PART_COLOR,
  aluminum: '#A8A9AD',
  aluminium: '#A8A9AD',

  // ── Wood ─────────────────────────────────────────────
  wood: '#966F33',
  oak: '#966F33',
  pine: '#D4A57A',
  cedar: '#A0522D',
  walnut: '#5C3317',
  mahogany: '#C04000',
  cherry_wood: '#9B2335',
  maple_wood: '#F0D080',
  teak: '#B8860B',
  bamboo_wood: '#8DB600',
  plywood: '#D2B48C',
  driftwood: '#B0977A',

  // ── Stone / minerals ─────────────────────────────────
  rock: '#A0A0A0',
  stone: '#9E9E9E',
  granite: '#808080',
  marble: '#F5F5F0',
  limestone: '#C8C0A8',
  sandstone: '#C2A27A',
  obsidian: '#2C2C2C',
  quartz: '#F5F5F5',
  amethyst: '#9B59B6',
  sapphire: '#1B4F72',
  ruby: '#C0392B',
  emerald: '#1A6B3C',
  jade: '#00A86B',
  turquoise: '#40E0D0',
  opal: '#E8D5B7',
  coal: '#1C1C1C',
  crystal: '#E0F0FF',

  // ── Sky / water / nature ─────────────────────────────
  sky: '#87CEEB',
  ocean: '#006994',
  sea: '#006994',
  water: '#1E90FF',
  lake: '#1E90FF',
  river: '#1E90FF',
  sand: '#C2B280',
  mud: '#6B4226',
  soil: '#7B5B3A',
  dirt: '#9B7653',
  clay: '#CC7357',
  snow: '#FFFAFA',
  ice: '#B0E0E6',
  fog: '#D8D8D8',
  ash: '#B2B2B2',
  lava: '#CF1020',
  magma: '#CF1020',
  fire: '#FF4500',
  flame: '#FF4500',
  smoke: '#808080',

  // ── Rubber / plastic / synthetics ────────────────────
  rubber: '#1C1C1C',
  tire: '#1C1C1C',
  tyre: '#1C1C1C',
  plastic: DEFAULT_PART_COLOR,
  nylon: '#E0E0E0',
  silicone: '#E0E0E0',
  glass: '#B0E0E6',
  ceramic: '#F5F5DC',
  porcelain: '#FFFFF0',
  concrete: '#9E9E9E',
  foam: '#F5F5F5',
  fabric: DEFAULT_PART_COLOR,
  leather: '#8B4513',
  suede: '#C2956C',
  felt: '#4F7942',
  denim: '#1560BD',
  cotton: '#FFFFF0',

  // ── Common everyday objects ───────────────────────────
  firetruck: '#FF0000',
  schoolbus: '#FFD700',
  trafficcone: '#FF6600',
  tennisball: '#CCFF00',
  basketball: '#E87722',
  football: '#8B4513',
  baseball: '#FFFACD',
  soccerball: '#1C1C1C',
  volleyball: '#F5DEB3',
  puck: '#1C1C1C',
  frisbee: '#E0E0E0',
  balloon: '#FF6B6B',
  pencil: '#FFD700',
  crayon: '#FF6B35',
  eraser: '#FFB6C1',
  ruler: '#FFDB58',
  book: '#8B4513',
  notebook: '#FFFACD',
  lemon_drop: '#FFF44F',
};

export function inferColor(text: string): string {
  if (!text || !text.trim()) return DEFAULT_PART_COLOR;

  const lower = text.toLowerCase();

  // 1. Check for explicit color adjectives first (e.g. "red car", "golden ring")
  for (const [name, hex] of Object.entries(EXPLICIT_COLORS)) {
    // Word-boundary match
    if (new RegExp(`\\b${name}\\b`).test(lower)) {
      return hex;
    }
  }

  // 2. Check object → color map against each word token
  const tokens = lower.split(/[\s,._\-/]+/);
  for (const token of tokens) {
    const clean = token.replace(/[^a-z]/g, '');
    if (OBJECT_COLORS[clean]) {
      return OBJECT_COLORS[clean];
    }
  }

  // 3. Try multi-word compounds (e.g. "fire truck", "school bus", "tennis ball")
  const compoundMap: Record<string, string> = {
    'fire truck': '#FF0000',
    'school bus': '#FFD700',
    'traffic cone': '#FF6600',
    'tennis ball': '#CCFF00',
    'basket ball': '#E87722',
    'soccer ball': '#1C1C1C',
    'foot ball': '#8B4513',
    'base ball': '#FFFACD',
  };
  for (const [phrase, hex] of Object.entries(compoundMap)) {
    if (lower.includes(phrase)) return hex;
  }

  // 4. Default: metallic grey for unknown/likely-metal parts
  return DEFAULT_PART_COLOR;
}
